import React from 'react';
import fr from 'facet-rest';
import PropTypes from 'prop-types';

import useComponentDidMount from 'Hooks/useComponentDidMount';
import { addQueryWithReplace, QueryVal } from 'Helpers/Query';
import { dispatchOnboardingNavigationRefresh } from 'Hooks/useRefreshEvent';

const SurveyContext = React.createContext(undefined);

export const STEP_LINK_QUERY_PARAM_NAME = 'stepLink';

// Actions
export const INITIALIZE_STARTED = 'initialize-started';
export const INITIALIZE_SUCCEEDED = 'initialize-succeeded';
export const INITIALIZE_FAILED = 'initialize-failed';

export const SET_ONBOARDING_STEP_COLLECTION = 'set-onboarding-step-collection';

export const ONBOARDING_STEP_LOADING_STARTED = 'next-onboarding-step-refresh-started';
export const ONBOARDING_STEP_LOADING_SUCCEEDED = 'next-onboarding-step-refresh-succeeded';
export const ONBOARDING_STEP_LOADING_FAILED = 'next-onboarding-step-refresh-failed';
export const ONBOARDING_STEP_SET_NAVBAR_VISIBILITY = 'set-onboarding-navbar-visibility';
export const ONBOARDING_STEP_SET_HEADER_VISIBILITY = 'set-onboarding-header-visibility';

const getOnboardingStepResourceFromCollection = (coll) => {
  let resource = null;
  if (coll !== null) {
    resource = coll.resources().head();
  }

  return resource;
};

const init = ({
  onboardingStepCollection,
  fixedFooter,
  showNavbar,
  showHeader,
  showFooter,
  showFullStep,
}) => {
  const stepLinkQueryParam = QueryVal(STEP_LINK_QUERY_PARAM_NAME, '');

  const returnValue = {
    showNavbar,
    showHeader,
    showFooter,
    showFullStep,
    fixedFooter,
    initialStepLink: stepLinkQueryParam,
    isInitializing: true,
    initializingError: null,
    onboardingStepCollection: null,
    onboardingStepResource: null,
    isOnboardingStepCollectionLoading: false,
    onboardingStepLoadingError: null,
  };

  // A note about the client dashboard...
  // We set onboardingStepCollection on load of the dashboard becuase it comes back with the dashboard resource
  // Clicking view all from the client dashboard should instantly load the survey because it already has the step
  // In the "View All" case we do not set the step link param.
  // If you click to it through the navigation drawer the set the query param and should disregard the onboardingStepCollection prop
  if (onboardingStepCollection && !stepLinkQueryParam) {
    return {
      ...returnValue,
      initialStepLink: onboardingStepCollection.link('self').url(),
      isInitializing: false,
      onboardingStepCollection,
      onboardingStepResource: onboardingStepCollection.resources().head(),
    };
  }

  return returnValue;
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case INITIALIZE_STARTED: {
      return {
        ...state,
        isInitializing: true,
        initializingError: null,
      };
    }
    case INITIALIZE_SUCCEEDED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: null,
        onboardingStepCollection: action.onboardingStepCollection,
        onboardingStepResource: getOnboardingStepResourceFromCollection(action.onboardingStepCollection),
      };
    }
    case INITIALIZE_FAILED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: action.initializationError,
      };
    }
    case SET_ONBOARDING_STEP_COLLECTION: {
      return {
        ...state,
        onboardingStepCollection: action.onboardingStepCollection,
        onboardingStepResource: getOnboardingStepResourceFromCollection(action.onboardingStepCollection),
      };
    }
    case ONBOARDING_STEP_LOADING_STARTED: {
      return {
        ...state,
        isOnboardingStepCollectionLoading: true,
        onboardingStepLoadingError: null,
      };
    }
    case ONBOARDING_STEP_LOADING_SUCCEEDED: {
      return {
        ...state,
        isOnboardingStepCollectionLoading: false,
        onboardingStepLoadingError: null,
        onboardingStepCollection: action.onboardingStepCollection,
        onboardingStepResource: getOnboardingStepResourceFromCollection(action.onboardingStepCollection),
      };
    }
    case ONBOARDING_STEP_LOADING_FAILED: {
      return {
        ...state,
        isOnboardingStepCollectionLoading: false,
        onboardingStepLoadingError: action.onboardingStepLoadingError,
      };
    }
    case ONBOARDING_STEP_SET_NAVBAR_VISIBILITY: {
      return {
        ...state,
        showNavbar: action.headerVisibility,
      };
    }
    case ONBOARDING_STEP_SET_HEADER_VISIBILITY: {
      return {
        ...state,
        showHeader: action.headerVisibility,
      };
    }
    default: {
      throw new Error(`Unhandled SurveyContext action type: ${action.type}`);
    }
  }
};

const SurveyProvider = ({
  clientResource,
  onboardingStepCollection: onboardingStepCollectionProp,
  fixedFooter,
  showNavbar,
  showHeader,
  showFooter,
  showFullStep,
  onStepChange,
  children,
}) => {
  const useReducerInitializerArg = {
    onboardingStepCollection: onboardingStepCollectionProp,
    fixedFooter,
    showNavbar,
    showHeader,
    showFooter,
    showFullStep,
  };
  const [state, dispatch] = React.useReducer(reducer, useReducerInitializerArg, init);

  const setNavbarVisibility = React.useCallback(async navbarVisibility => {
    dispatch({
      type: ONBOARDING_STEP_SET_NAVBAR_VISIBILITY,
      navbarVisibility,
    });
  }, [state]);

  const setHeaderVisibility = React.useCallback(async headerVisibility => {
    dispatch({
      type: ONBOARDING_STEP_SET_HEADER_VISIBILITY,
      headerVisibility,
    });
  }, [state]);

  const loadOnboardingStepCollection = React.useCallback(async (link) => {
    try {
      if (!link) {
        return;
      }

      dispatch({ type: ONBOARDING_STEP_LOADING_STARTED });

      const coll = await link.get();
      const [resource] = coll;
      addQueryWithReplace({ [STEP_LINK_QUERY_PARAM_NAME]: resource.link('self').url() });

      if (onStepChange) {
        onStepChange(resource, state.onboardingStepResource);
      }

      dispatch({
        type: ONBOARDING_STEP_LOADING_SUCCEEDED,
        onboardingStepCollection: coll,
      });
    } catch (err) {
      dispatch({ type: ONBOARDING_STEP_LOADING_FAILED, loadError: err });
    }
  }, [state]);

  const loadPreviousOnboardingStepCollection = React.useCallback(async () => {
    const link = state.onboardingStepResource?.link('prev');

    return loadOnboardingStepCollection(link);
  }, [state, loadOnboardingStepCollection]);

  const loadNextOnboardingStepCollection = React.useCallback(async () => {
    const link = state.onboardingStepResource?.link('next');

    return loadOnboardingStepCollection(link);
  }, [state, loadOnboardingStepCollection]);

  const refreshOnboardingStepCollection = React.useCallback(async () => {
    const link = state.onboardingStepResource?.link('self');

    return loadOnboardingStepCollection(link);
  }, [state, loadOnboardingStepCollection]);

  const setOnboardingStepCollection = React.useCallback(async (onboardingStepCollection) => {
    if (!setOnboardingStepCollection) {
      throw new Error('Developer error, must provide onboarding step collection');
    }

    const [onboardingStepRes] = onboardingStepCollection;

    addQueryWithReplace({ [STEP_LINK_QUERY_PARAM_NAME]: onboardingStepRes.link('self').url() });

    if (onStepChange) {
      onStepChange(onboardingStepRes, state.onboardingStepResource);
    }

    dispatch({
      type: SET_ONBOARDING_STEP_COLLECTION,
      onboardingStepCollection,
    });

    dispatchOnboardingNavigationRefresh();
  }, []);

  useComponentDidMount(async () => {
    try {
      // Return early if we don't need to load the initial step
      if (!state.isInitializing) {
        return;
      }

      dispatch({ type: INITIALIZE_STARTED });

      const linkURL = state.initialStepLink || clientResource?.relationship(fr.relationships.nextOnboardingStep.name)?.link('related')?.url();
      if (!linkURL) {
        dispatch({
          type: INITIALIZE_SUCCEEDED,
          onboardingStepCollection: null,
        });

        return;
      }

      const nextOnboardingStepResp = await fr.http.get(linkURL);
      dispatch({
        type: INITIALIZE_SUCCEEDED,
        onboardingStepCollection: nextOnboardingStepResp,
      });

      const [onboardingStepResource] = nextOnboardingStepResp;
      addQueryWithReplace({ [STEP_LINK_QUERY_PARAM_NAME]: onboardingStepResource.link('self').url() });
    } catch (err) {
      dispatch({ type: INITIALIZE_STARTED, initializationError: err });
    }
  });

  const memoizedValue = React.useMemo(() => {
    return {
      showNavbar: state.showNavbar,
      showHeader: state.showHeader,
      fixedFooter: state.fixedFooter,
      showFooter: state.showFooter,
      showFullStep: state.showFullStep,
      isInitializing: state.isInitializing,
      initializingError: state.initializingError,
      onboardingStepCollection: state.onboardingStepCollection,
      onboardingStepResource: state.onboardingStepResource,
      isOnboardingStepCollectionLoading: state.isOnboardingStepCollectionLoading,
      onboardingStepLoadingError: state.onboardingStepLoadingError,
      surveyModuleTitle: state.onboardingStepResource?.attr('moduleName'),
      isEmbeddedWizard: state.onboardingStepResource?.type() === 'onboardingEmbeddedWizard',
      refreshOnboardingStepCollection,
      loadOnboardingStepCollection,
      loadPreviousOnboardingStepCollection,
      loadNextOnboardingStepCollection,
      setOnboardingStepCollection,
      setNavbarVisibility,
      setHeaderVisibility,
    };
  }, [
    state,
    refreshOnboardingStepCollection,
    loadOnboardingStepCollection,
    loadPreviousOnboardingStepCollection,
    loadNextOnboardingStepCollection,
    setOnboardingStepCollection,
    setNavbarVisibility,
    setHeaderVisibility,
  ]);

  return (
    <SurveyContext.Provider value={memoizedValue}>
      {children}
    </SurveyContext.Provider>
  );
};

// Use Survey State Context
const useSurveyContext = () => {
  const context = React.useContext(SurveyContext);
  if (context === undefined) {
    throw new Error('useSurveyContext must be used within a SurveyProvider');
  }
  return context;
};

SurveyProvider.defaultProps = {
  onboardingStepCollection: null,
  onStepChange: null,
  fixedFooter: true,
  showNavbar: true,
  showHeader: true,
  showFooter: true,
  showFullStep: true,
};

SurveyProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  clientResource: PropTypes.object.isRequired,
  onboardingStepCollection: PropTypes.object,
  fixedFooter: PropTypes.bool,
  showNavbar: PropTypes.bool,
  showHeader: PropTypes.bool,
  showFooter: PropTypes.bool,
  showFullStep: PropTypes.bool,
  onStepChange: PropTypes.func,
};

export {
  SurveyProvider,
  useSurveyContext,
};
